import React, { useState } from 'react';
import { AlertCircle, CheckCircle, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import StepPlayerInfo from './registration/StepPlayerInfo';
import StepParentInfo from './registration/StepParentInfo';
import StepEmergencyMedical from './registration/StepEmergencyMedical';
import StepAdditional from './registration/StepAdditional';
import StepReview from './registration/StepReview';
import SuccessScreen from './registration/SuccessScreen';
import { MYREC_URL } from './registration/constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOTAL_STEPS = 5;
const STEP_LABELS = ['Player', 'Parent', 'Emergency', 'Additional', 'Review'];

const INITIAL_FORM = {
  // Player Info
  player_first_name: '', player_last_name: '', date_of_birth: '', age: '', gender: '',
  // Program Selection
  program: '', team_age_group: '',
  // Parent Info
  parent_first_name: '', parent_last_name: '', email: '', phone: '',
  // Address
  address: '', city: '', state: 'MD', zip_code: '',
  // Emergency Contact
  emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relation: '',
  // Medical
  medical_conditions: '', allergies: '', medications: '',
  insurance_provider: '', insurance_policy_number: '',
  // Additional
  tshirt_size: '', previous_experience: '', returning_player: false, discount_type: '',
};

const calculateAge = (dob) => {
  const date = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) age--;
  return age;
};

const validateStep = (stepNum, formData) => {
  switch (stepNum) {
    case 1:
      return formData.player_first_name && formData.player_last_name &&
             formData.date_of_birth && formData.gender &&
             formData.program && formData.team_age_group;
    case 2:
      return formData.parent_first_name && formData.parent_last_name &&
             formData.email && formData.phone &&
             formData.address && formData.city && formData.zip_code;
    case 3:
      return formData.emergency_contact_name && formData.emergency_contact_phone &&
             formData.emergency_contact_relation;
    case 4:
      return Boolean(formData.tshirt_size);
    default:
      return true;
  }
};

const RegistrationForm = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'date_of_birth' && value) next.age = calculateAge(value);
      if (name === 'program') next.team_age_group = '';
      return next;
    });
  };

  const nextStep = () => {
    if (validateStep(step, formData)) {
      setStep((s) => Math.min(s + 1, TOTAL_STEPS));
      setError('');
    } else {
      setError('Please fill in all required fields');
    }
  };

  const prevStep = () => {
    setStep((s) => Math.max(s - 1, 1));
    setError('');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, age: parseInt(formData.age, 10) }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(data);
        if (onSuccess) onSuccess(data);
      } else {
        setError(data.message || 'Registration failed. Please try again.');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) return <SuccessScreen formData={formData} success={success} onClose={onClose} />;

  return (
    <div className="registration-form" data-testid="registration-form">
      <div className="form-header">
        <h2>Get More Info &mdash; 2026 Season</h2>
        <p className="form-subheading">
          This is an interest form so a Panthers coordinator can reach you.
          Official registration &amp; payment happen on the
          <a
            href={MYREC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="form-myrec-link"
          > My Rec portal</a>.
        </p>

        <div className="step-indicator">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((s) => (
            <div key={s} className={`step-dot ${step >= s ? 'active' : ''} ${step === s ? 'current' : ''}`}>
              {step > s ? <CheckCircle size={16} /> : s}
            </div>
          ))}
        </div>

        <div className="step-labels">
          {STEP_LABELS.map((label, i) => (
            <span key={label} className={step === i + 1 ? 'active' : ''}>{label}</span>
          ))}
        </div>
      </div>

      {error && (
        <div className="form-error">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {step === 1 && <StepPlayerInfo formData={formData} onChange={handleChange} />}
      {step === 2 && <StepParentInfo formData={formData} onChange={handleChange} />}
      {step === 3 && <StepEmergencyMedical formData={formData} onChange={handleChange} />}
      {step === 4 && <StepAdditional formData={formData} onChange={handleChange} />}
      {step === 5 && <StepReview formData={formData} />}

      <div className="form-navigation">
        {step > 1 && (
          <Button onClick={prevStep} className="prev-btn" disabled={loading} data-testid="reg-prev-btn">
            Back
          </Button>
        )}
        {step < TOTAL_STEPS ? (
          <Button onClick={nextStep} className="next-btn" data-testid="reg-next-btn">
            Continue
            <ChevronRight size={18} />
          </Button>
        ) : (
          <Button onClick={handleSubmit} className="submit-btn" disabled={loading} data-testid="reg-submit-btn">
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Interest'
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default RegistrationForm;
